import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReorderIcon from '@mui/icons-material/Reorder';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { getUsersByCompanyPaginated, getCompanyEmployeeStats } from 'services/firebaseServices';
import { getProfile } from 'utilities';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { fireStoreDB } from 'services';

// ── CSV helpers ────────────────────────────────────────────────────────────────

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, '').toLowerCase());
  return lines.slice(1).map((line) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') { inQuotes = !inQuotes; }
      else if (line[i] === ',' && !inQuotes) { values.push(current.trim()); current = ''; }
      else { current += line[i]; }
    }
    values.push(current.trim());
    const row = {};
    headers.forEach((h, i) => { row[h] = (values[i] || '').replace(/^"|"$/g, '').trim(); });
    return row;
  });
}

function normaliseRow(row) {
  const get = (...keys) => {
    for (const k of keys) {
      const val = row[k] || row[k.replace(/ /g, '_')] || row[k.replace(/ /g, '')];
      if (val) return val;
    }
    return '';
  };
  return {
    firstName: get('first name', 'firstname', 'first_name', 'name'),
    lastName: get('last name', 'lastname', 'last_name', 'surname'),
    email: get('email', 'email address', 'emailaddress', 'e-mail'),
    phone: get('phone', 'phone number', 'phonenumber', 'mobile', 'mobile number', 'tel', 'telephone'),
  };
}

// ── Stat widget ────────────────────────────────────────────────────────────────

function StatWidget({ icon, label, value, color }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: '1px solid #e5e7eb',
        bgcolor: 'white',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          bgcolor: `${color}18`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 0.5,
        }}
      >
        {React.cloneElement(icon, { sx: { color, fontSize: 22 } })}
      </Box>
      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', lineHeight: 1 }}>
        {value}
      </Typography>
    </Paper>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function EmployeesComponent() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [employees, setEmployees] = useState([]);
  const [businessData, setBusinessData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employeeStats, setEmployeeStats] = useState({ total: 0, ordered: 0, reordered: 0 });
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // CSV upload state
  const fileInputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [csvError, setCsvError] = useState('');
  const [pendingFile, setPendingFile] = useState(null); // { name, rows }
  const [uploadStats, setUploadStats] = useState(null); // live counters during upload

  // ── Data fetching ────────────────────────────────────────────────────────────

  const fetchBusinessInfo = async () => {
    try {
      const profile = getProfile();
      if (!profile?.uid) return;
      const q = query(collection(fireStoreDB, 'businesses'), where('userId', '==', profile.uid));
      const snapshot = await getDocs(q);
      if (!snapshot.empty) setBusinessData(snapshot.docs[0].data());
    } catch (error) {
      console.error('Error fetching business info:', error);
    }
  };

  const mapEmployee = (user) => ({
    id: user.id,
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    email: user.email || '',
    hasOrdered: user.cardOrderStatus === 'Registered',
    hasReordered: user.cardReOrderStatus === 'Registered',
    cardOrderDate: user.cardOrderDate || '',
    cardReOrderDate: user.cardReOrderDate || '',
  });

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      if (!businessData?.companyName) return;
      // Fetch first page (50 employees) and counts in parallel.
      // getCompanyEmployeeStats uses getCountFromServer() — 0 document reads,
      // so stat widgets are always accurate even with pagination.
      const [page, stats] = await Promise.all([
        getUsersByCompanyPaginated(businessData.companyName, 50, null),
        getCompanyEmployeeStats(businessData.companyName),
      ]);
      setEmployees(page.users.map(mapEmployee));
      setLastDoc(page.lastDoc);
      setHasMore(page.hasMore);
      setEmployeeStats(stats);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreEmployees = async () => {
    if (!lastDoc || loadingMore) return;
    try {
      setLoadingMore(true);
      const page = await getUsersByCompanyPaginated(businessData.companyName, 50, lastDoc);
      setEmployees((prev) => [...prev, ...page.users.map(mapEmployee)]);
      setLastDoc(page.lastDoc);
      setHasMore(page.hasMore);
    } catch (error) {
      console.error('Error loading more employees:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { fetchBusinessInfo(); }, []);
  useEffect(() => { if (businessData?.companyName) fetchEmployees(); }, [businessData?.companyName]); // eslint-disable-line

  const getSignUpUrl = () => {
    if (!businessData?.companyName) return '';
    const slug = businessData.companyName.toLowerCase().replace(/\s+/g, '-');
    return `${window.location.origin}/${slug}/sign-up`;
  };

  // ── CSV processing ───────────────────────────────────────────────────────────

  const processFile = useCallback(async (file) => {
    setCsvError('');
    setUploadResult(null);
    setPendingFile(null);

    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      setCsvError('Please upload a .csv file.');
      return;
    }

    const text = await file.text();
    const rows = parseCSV(text);
    const validRows = rows.map(normaliseRow).filter((r) => r.email);

    if (validRows.length === 0) {
      setCsvError('No valid email addresses found. Make sure your CSV has an "Email" column.');
      return;
    }

    setPendingFile({ name: file.name, rows: validRows });
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (file) processFile(file);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = () => setDragging(false);

  // Send 50 emails per API call using Resend's batch endpoint.
  // 3000 emails = 60 HTTP requests instead of 3000 — stays well under rate limits.
  const INVITE_BATCH_SIZE = 50;

  const sendInvites = async () => {
    if (!pendingFile) return;
    const { rows } = pendingFile;
    const companyName = businessData?.companyName || 'your company';
    const signUpUrl   = getSignUpUrl();
    const startTime   = Date.now();

    setUploading(true);
    setUploadProgress(0);
    setUploadStats({ sent: 0, failed: 0, total: rows.length });
    setPendingFile(null);

    let sent   = 0;
    let failed = 0;
    const inviteLog = []; // full per-email record for the error report

    for (let i = 0; i < rows.length; i += INVITE_BATCH_SIZE) {
      const batchRows = rows.slice(i, i + INVITE_BATCH_SIZE);
      const t0 = Date.now();

      // Build the batch payload expected by the edge function
      const batchPayload = batchRows.map(({ firstName, email, phone }) => ({
        toEmail: email, firstName, phone, companyName, signUpUrl,
      }));

      try {
        const res = await fetch('/api/send-invite-email', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ batch: batchPayload }),
        });
        const durationMs  = Date.now() - t0;
        const data        = await res.json().catch(() => ({}));

        if (res.ok) {
          // All emails in the batch succeeded
          sent += batchRows.length;
          batchRows.forEach(({ email, firstName }) => inviteLog.push({
            email, firstName,
            status: 'sent', statusCode: 200, durationMs,
            error: null, timestamp: new Date().toISOString(),
          }));
        } else {
          // Whole batch failed (rate limit, auth error, etc.)
          failed += batchRows.length;
          const errMsg = data.error || `HTTP ${res.status}`;
          batchRows.forEach(({ email, firstName }) => inviteLog.push({
            email, firstName,
            status: 'failed', statusCode: res.status, durationMs,
            error: errMsg, timestamp: new Date().toISOString(),
          }));
        }
      } catch (err) {
        // Network error — mark whole batch as failed
        failed += batchRows.length;
        batchRows.forEach(({ email, firstName }) => inviteLog.push({
          email, firstName,
          status: 'failed', statusCode: null, durationMs: Date.now() - t0,
          error: err.message, timestamp: new Date().toISOString(),
        }));
      }

      // Update live progress counters after each batch
      const processed = Math.min(i + INVITE_BATCH_SIZE, rows.length);
      setUploadProgress(Math.round((processed / rows.length) * 100));
      setUploadStats({ sent, failed, total: rows.length });
    }

    const totalDurationMs = Date.now() - startTime;
    const avgDurationMs   = inviteLog.length
      ? Math.round(inviteLog.reduce((s, l) => s + (l.durationMs || 0), 0) / inviteLog.length)
      : 0;

    setUploading(false);
    setUploadStats(null);
    setUploadResult({
      sent,
      failed,
      total:          rows.length,
      totalDurationMs,
      avgDurationMs,
      inviteLog,
      errors: inviteLog.filter((l) => l.status === 'failed').map((l) => `${l.email}: ${l.error}`),
    });
  };

  // Download a CSV error report after sending
  const downloadInviteReport = () => {
    if (!uploadResult?.inviteLog) return;
    const headers = ['Email', 'First Name', 'Status', 'HTTP Status', 'Duration (ms)', 'Error', 'Timestamp'];
    const dataRows = uploadResult.inviteLog.map((e) => [
      e.email,
      e.firstName,
      e.status,
      e.statusCode  ?? '',
      e.durationMs  ?? '',
      e.error       ?? '',
      e.timestamp,
    ]);
    const csv = [headers, ...dataRows]
      .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `invite-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Stats ────────────────────────────────────────────────────────────────────

  // Use server-side counts (from getCountFromServer) so stat widgets
  // show the true total even when only the first 50 employees are loaded.
  const totalEmployees = employeeStats.total;
  const totalOrdered   = employeeStats.ordered;
  const totalReordered = employeeStats.reordered;

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: { xs: 1, md: 2 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937', mb: 3 }}>
        Employees
      </Typography>

      <Grid container spacing={3}>

        {/* ── Stat widgets ── */}
        <Grid item xs={12} sm={4}>
          <StatWidget
            icon={<PeopleIcon />}
            label="Total Employees Invited"
            value={totalEmployees}
            color="#3b82f6"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatWidget
            icon={<CreditCardIcon />}
            label="Ordered Cards"
            value={totalOrdered}
            color="#10b981"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatWidget
            icon={<ReorderIcon />}
            label="Reordered Cards"
            value={totalReordered}
            color="#f59e0b"
          />
        </Grid>

        {/* ── CSV Upload widget ── */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{ p: 3, borderRadius: 3, border: '1px solid #e5e7eb', bgcolor: 'white' }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 0.5 }}>
              Invite Employees via CSV
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ color: '#6b7280', flex: 1 }}>
                Upload a CSV with columns: <strong>First Name</strong>, <strong>Last Name</strong>, <strong>Email</strong>, <strong>Phone</strong>. Each employee will receive an invitation email with a sign-up link.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  const csv = 'First Name,Last Name,Email,Phone\nJane,Doe,jane.doe@example.com,+44 20 1234 5678\nJohn,Smith,john.smith@example.com,+47 21 23 45 67';
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'employee-invite-template.csv';
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                sx={{ textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap', borderColor: '#d1d5db', color: '#374151' }}
              >
                Download Template
              </Button>
            </Box>

            {/* Drop zone */}
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => !uploading && fileInputRef.current?.click()}
              sx={{
                border: `2px dashed ${dragging ? '#2563eb' : '#d1d5db'}`,
                borderRadius: 3,
                bgcolor: dragging ? '#eff6ff' : '#f9fafb',
                p: { xs: 4, md: 6 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                cursor: uploading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: '#2563eb',
                  bgcolor: '#eff6ff',
                },
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: '#dbeafe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CloudUploadIcon sx={{ color: '#2563eb', fontSize: 28 }} />
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#374151' }}>
                Drag & drop your CSV here
              </Typography>
              <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                or click to browse
              </Typography>
            </Box>

            {/* File selected preview */}
            {pendingFile && !uploading && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid #e5e7eb',
                  bgcolor: '#f9fafb',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <InsertDriveFileIcon sx={{ color: '#2563eb' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                    {pendingFile.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>
                    {pendingFile.rows.length} employee{pendingFile.rows.length !== 1 ? 's' : ''} found
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={sendInvites}
                  sx={{
                    bgcolor: '#2563eb',
                    '&:hover': { bgcolor: '#1d4ed8' },
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  Send Invitations
                </Button>
                <Button
                  variant="text"
                  onClick={() => setPendingFile(null)}
                  sx={{ textTransform: 'none', color: '#6b7280' }}
                >
                  Cancel
                </Button>
              </Box>
            )}

            {/* Upload progress */}
            {uploading && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Sending invitations… {uploadStats && `(${uploadStats.sent + uploadStats.failed} / ${uploadStats.total})`}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 600 }}>
                    {uploadProgress}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{ borderRadius: 2, height: 8 }}
                />
                {uploadStats && (
                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 600 }}>
                      ✓ {uploadStats.sent} sent
                    </Typography>
                    {uploadStats.failed > 0 && (
                      <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 600 }}>
                        ✗ {uploadStats.failed} failed
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* CSV error */}
            {csvError && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }} onClose={() => setCsvError('')}>
                {csvError}
              </Alert>
            )}

            {/* Upload result */}
            {uploadResult && (
              <Box sx={{ mt: 2 }}>
                <Alert
                  severity={uploadResult.failed === 0 ? 'success' : 'warning'}
                  sx={{ borderRadius: 2 }}
                  onClose={() => setUploadResult(null)}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {uploadResult.sent} invitation{uploadResult.sent !== 1 ? 's' : ''} sent
                    {uploadResult.failed > 0 && `, ${uploadResult.failed} failed`}
                    {' — '}completed in {uploadResult.totalDurationMs >= 60000
                      ? `${Math.round(uploadResult.totalDurationMs / 60000)}m ${Math.round((uploadResult.totalDurationMs % 60000) / 1000)}s`
                      : `${(uploadResult.totalDurationMs / 1000).toFixed(1)}s`
                    }
                    {uploadResult.avgDurationMs > 0 && ` (avg ${uploadResult.avgDurationMs}ms/email)`}.
                  </Typography>
                  {uploadResult.errors.length > 0 && (
                    <Box component="ul" sx={{ mt: 0.5, mb: 0.5, pl: 2 }}>
                      {uploadResult.errors.slice(0, 5).map((e, i) => (
                        <li key={i}><Typography variant="caption">{e}</Typography></li>
                      ))}
                      {uploadResult.errors.length > 5 && (
                        <li><Typography variant="caption">…and {uploadResult.errors.length - 5} more (download report to see all)</Typography></li>
                      )}
                    </Box>
                  )}
                </Alert>
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={downloadInviteReport}
                  sx={{ mt: 1, textTransform: 'none', fontWeight: 600, color: '#2563eb', borderColor: '#2563eb', borderRadius: 2 }}
                  variant="outlined"
                >
                  Download Full Report ({uploadResult.total} rows)
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* ── Employee list widget ── */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{ p: 3, borderRadius: 3, border: '1px solid #e5e7eb', bgcolor: 'white' }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 2 }}>
              Employee List
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {['Name', 'Email', 'Ordered Card', 'Reordered Card',
                      ...(!isMobile ? ['Order Date', 'Reorder Date'] : [])
                    ].map((col, i) => (
                      <TableCell
                        key={col}
                        align={i >= 2 && i <= 3 ? 'center' : 'left'}
                        sx={{ fontWeight: 600, bgcolor: '#f9fafb', color: '#374151', border: 'none' }}
                      >
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 4 : 6} align="center" sx={{ py: 6, border: 'none' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <PeopleIcon sx={{ fontSize: 40, color: '#d1d5db' }} />
                          <Typography variant="body1" color="text.secondary">
                            No employees yet
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                            Upload a CSV above to invite your first employees.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    employees.map((emp) => (
                      <TableRow key={emp.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                        <TableCell sx={{ color: '#1f2937', fontWeight: 500 }}>
                          {emp.firstName} {emp.lastName}
                        </TableCell>
                        <TableCell sx={{ color: '#6b7280' }}>{emp.email}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={emp.hasOrdered ? 'Yes' : 'No'}
                            color={emp.hasOrdered ? 'primary' : 'default'}
                            size="small"
                            variant={emp.hasOrdered ? 'filled' : 'outlined'}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={emp.hasReordered ? 'Yes' : 'No'}
                            color={emp.hasReordered ? 'success' : 'default'}
                            size="small"
                            variant={emp.hasReordered ? 'filled' : 'outlined'}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        {!isMobile && (
                          <>
                            <TableCell sx={{ color: '#6b7280' }}>{emp.cardOrderDate || '-'}</TableCell>
                            <TableCell sx={{ color: '#6b7280' }}>{emp.cardReOrderDate || '-'}</TableCell>
                          </>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Load more */}
            {(hasMore || loadingMore) && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={loadMoreEmployees}
                  disabled={loadingMore}
                  startIcon={loadingMore ? <CircularProgress size={16} /> : null}
                  sx={{ textTransform: 'none', borderRadius: 2, borderColor: '#d1d5db', color: '#374151' }}
                >
                  {loadingMore
                    ? 'Loading…'
                    : `Load more (${employeeStats.total - employees.length} remaining)`}
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

      </Grid>
    </Box>
  );
}
